/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    EventEmitter,
    Output,
} from '@angular/core';

import { ClrLoadingState } from '@cds/core/button';
import { IHttpResponse } from 'angular';
import { L10nService } from '@vmw/ngx-vip';
import { LicensingService } from 'ajs/modules/licensing';

import { AlertType, ILicenseAddAlert } from '../../licensing.types';
import './license-add-panel-modal.less';
import * as l10n from './license-add-panel-modal.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

interface IAddLicenseKeyErrorResponse {
    result: string;
}

/**
 * @description Dialog component for adding a license using license key.
 * @author Shubham Bindal
 */
@Component({
    selector: 'license-add-panel-modal',
    templateUrl: './license-add-panel-modal.component.html',
})
export class LicenseAddPanelModalComponent {
    /**
     * Emit on close/cancel.
     */
    @Output()
    public onClose = new EventEmitter<void>();

    /**
     * Emit when license is added successfully.
     */
    @Output()
    public onLicenseAdded = new EventEmitter<ILicenseAddAlert>();

    public readonly l10nKeys = l10nKeys;

    public loadingState: ClrLoadingState = ClrLoadingState.default;

    /**
     * New license key model.
     */
    public newLicenseKey = '';

    public error = '';

    constructor(
        private readonly licensingService: LicensingService,
        private readonly l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    public handleClose(): void {
        this.onClose.emit();
    }

    /**
     * Apply the new license key.
     */
    public applyNewLicenseKey(): void {
        this.loadingState = ClrLoadingState.loading;
        this.error = '';

        this.licensingService.applyLicenseKey(this.newLicenseKey)
            .then(() => {
                this.newLicenseKey = '';
                this.onLicenseAdded.emit({
                    type: AlertType.success,
                    message: this.l10nService.getMessage(l10nKeys.licenseAddSuccessMessage),
                });
            })
            .catch(({ data: { result } }: IHttpResponse<IAddLicenseKeyErrorResponse>) => {
                this.error = result;
            })
            .finally(() => {
                this.loadingState = ClrLoadingState.default;
            });
    }
}
